import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../app/providers.js';
import { isSignedIn } from '@plathunt/types';

const MIN_PASSWORD_LENGTH = 8;

export function Register() {
  const { signUp, state, authAvailable } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isSignedIn(state)) return <Navigate to="/profile" replace />;

  /** Validierung im Client. Supabase prueft dieselben Regeln noch einmal serverseitig. */
  function validate(): string | null {
    if (!email.trim()) return 'Bitte eine E-Mail eintragen.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return 'Die E-Mail sieht nicht gültig aus.';
    if (password.length < MIN_PASSWORD_LENGTH) {
      return `Das Passwort braucht mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`;
    }
    if (password !== confirm) return 'Die beiden Passwörter stimmen nicht überein.';
    return null;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);
    const result = await signUp(email.trim(), password);
    setBusy(false);

    if (!result.ok) {
      setError(result.message ?? 'Registrierung fehlgeschlagen.');
      return;
    }
    setSuccess(result.message ?? 'Konto angelegt. Du bist angemeldet.');
    setPassword('');
    setConfirm('');
  }

  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <section className="ph-auth">
      <p className="ph-auth__kicker">PlatHunt Account</p>
      <h1 className="ph-screen__title">Account erstellen</h1>
      <p className="ph-screen__note">
        Ein Konto synchronisiert deinen Fortschritt. Ohne Konto bleibt alles lokal auf diesem Gerät.
        Das PSN-Konto ist davon getrennt und kommt später dazu.
      </p>

      {!authAvailable && (
        <p className="ph-notice ph-notice--error">
          Supabase ist nicht konfiguriert. Die App läuft auf DEV FIXTURES, eine Registrierung ist
          hier nicht möglich.
        </p>
      )}

      {error && (
        <p className="ph-notice ph-notice--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="ph-notice ph-notice--success" role="status">
          {success}
        </p>
      )}

      <form onSubmit={submit} noValidate>
        <label className="ph-field">
          <span className="ph-field__label">E-Mail</span>
          <input
            className="ph-input"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy || !authAvailable}
          />
        </label>

        <label className="ph-field">
          <span className="ph-field__label">Passwort</span>
          <input
            className="ph-input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy || !authAvailable}
          />
          <span className="ph-field__hint">Mindestens {MIN_PASSWORD_LENGTH} Zeichen.</span>
        </label>

        <label className="ph-field">
          <span className="ph-field__label">Passwort bestätigen</span>
          <input
            className="ph-input"
            type="password"
            autoComplete="new-password"
            aria-invalid={mismatch}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy || !authAvailable}
          />
          {mismatch && (
            <span className="ph-field__error">Die Passwörter stimmen nicht überein.</span>
          )}
        </label>

        <button className="ph-button" type="submit" disabled={busy || !authAvailable}>
          {busy ? 'Konto wird angelegt …' : 'Account erstellen'}
        </button>
      </form>

      <div className="ph-authlinks">
        <Link to="/login">Schon ein Konto? Anmelden</Link>
        <Link to="/">Ohne Konto weitermachen</Link>
      </div>
    </section>
  );
}
